import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import type { TrafficStatus } from '@shared/ledger';

import { Switch } from '@/components/ui/Switch';
import { InlineError } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useTrafficStatus } from '@/lib/queries';
import { useSetTraffic } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import { usdt } from '@/lib/format';
import { TRAFFIC_BLOCK_COPY } from '@/lib/moneyLabels';
import { cn } from '@/lib/cn';

/**
 * The switch a trader reaches for when they go to sleep, run out of card
 * balance, or leave the house — and, until now, could not reach at all, so
 * payments kept arriving and expiring against them.
 *
 * It is the loudest thing on Обзор after the verification prompt, and its
 * state is stated in words at page-title size. A trader glancing at their
 * phone from across the room has to get the answer in one second, and «включён»
 * versus «выключен» does that in a way a green dot does not.
 *
 * The one rule that must never break: this panel does not say «Приём включён»
 * unless the server says `canReceive`. The switch is only one of four things
 * that can stop routing, and a trader who believes they are online while the
 * router is skipping them will sit and wait for payments that are not coming.
 */

/** What the panel is actually saying. Derived once, rendered once. */
type View = 'receiving' | 'off' | 'blocked' | 'paused';

function viewFor(status: TrafficStatus): View {
  if (status.canReceive) return 'receiving';
  if (status.blockedBy && status.blockedBy !== 'traffic_disabled') return 'blocked';
  if (!status.isEnabled) return 'off';
  // The server says routing is off but names no reason we know. Say so plainly
  // rather than falling through to «включён».
  return 'paused';
}

const PLATE: Record<View, string> = {
  receiving: 'border-ok-500 bg-ok-50',
  off: 'border-line-strong bg-sunken',
  blocked: 'border-warn-500 bg-warn-50',
  paused: 'border-warn-500 bg-warn-50',
};

const DOT: Record<View, string> = {
  receiving: 'bg-ok-500',
  off: 'bg-fg-muted',
  blocked: 'bg-warn-500',
  paused: 'bg-warn-500',
};

export function TrafficPanel({ className }: { className?: string }) {
  const traffic = useTrafficStatus();
  const setTraffic = useSetTraffic();
  const [failure, setFailure] = useState<string | null>(null);

  if (traffic.isPending) {
    return (
      <section
        role="status" aria-busy="true"
        className={cn('rounded-md border border-line bg-surface px-4 py-4 sm:px-5', className)}
      >
        <span className="sr-only">Загружаю состояние приёма платежей</span>
        <Skeleton className="h-2.5 w-32" />
        <Skeleton className="mt-3 h-7 w-48" />
        <Skeleton className="mt-3 h-3.5 w-64" />
      </section>
    );
  }

  if (traffic.isError) {
    return (
      <InlineError
        className={className}
        message={toMessage(
          traffic.error,
          'Не удалось узнать, идут ли вам платежи. Обновите страницу.',
        )}
        onRetry={() => void traffic.refetch()}
      />
    );
  }

  const status = traffic.data;
  const view = viewFor(status);
  const block = status.blockedBy ? TRAFFIC_BLOCK_COPY[status.blockedBy] : null;

  const title =
    view === 'receiving'
      ? 'Приём включён'
      : view === 'off'
        ? 'Приём выключен'
        : view === 'blocked' && block
          ? block.title
          : 'Приём приостановлен';

  const body =
    view === 'receiving'
      ? 'Новые платежи направляются вам.'
      : view === 'off'
        ? 'Новые платежи вам не направляются.'
        : view === 'blocked' && block
          ? block.body
          : 'Сервер не направляет вам платежи, но не назвал причину. Обновите страницу, а если ничего не изменится — напишите в поддержку.';

  const toggle = (next: boolean): void => {
    setFailure(null);
    setTraffic.mutate(next, {
      onError: (error) =>
        setFailure(
          toMessage(error, 'Не удалось изменить приём платежей. Состояние осталось прежним.'),
        ),
    });
  };

  return (
    <section
      aria-labelledby="traffic-heading"
      className={cn('rounded-md border px-4 py-4 sm:px-5 sm:py-5', PLATE[view], className)}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 id="traffic-heading" className="eyebrow text-fg-secondary">
            Приём платежей
          </h2>

          {/* The state, in words, at the largest size on this panel. Colour is
              a second signal only — the plate and the dot repeat what the word
              already says. */}
          <p className="mt-1.5 flex items-center gap-2.5 text-xl font-semibold leading-tight tracking-tight text-fg">
            <span
              aria-hidden="true"
              className={cn('h-2.5 w-2.5 shrink-0 rounded-full', DOT[view])}
            />
            <span className="min-w-0">{title}</span>
          </p>

          <p id="traffic-state" className="mt-2 max-w-prose text-base leading-normal text-fg-secondary">
            {body}
          </p>

          {/* A block that outranks the trader's own switch hides the fact that
              the switch is also off. Say it, or turning the block into a fixed
              account will look like it changed nothing. */}
          {view === 'blocked' && !status.isEnabled ? (
            <p className="mt-1.5 max-w-prose text-sm text-fg-secondary">
              Кроме того, ваш переключатель выключен.
            </p>
          ) : null}

          {view === 'receiving' || view === 'blocked' ? (
            <p className="num mt-2 text-xs text-fg-secondary">
              Свободный залог: {usdt(status.routableCapacity)}
            </p>
          ) : null}
        </div>

        <Switch
          checked={status.isEnabled}
          onChange={toggle}
          disabled={setTraffic.isPending}
          label="Приём платежей"
          describedBy="traffic-state"
        />
      </div>

      {block?.href ? (
        <Link
          to={block.href}
          className={cn(
            'focus-ring mt-4 flex h-11 w-full select-none items-center justify-center gap-2',
            'rounded-md border border-accent-600 bg-accent-600 px-4 text-base font-medium',
            'text-fg-inverse no-underline transition-colors duration-fast ease-out',
            'hover:border-accent-700 hover:bg-accent-700',
            'active:border-accent-900 active:bg-accent-900',
            'sm:inline-flex sm:w-auto sm:px-5',
          )}
        >
          {block.action}
          <ArrowRight size={15} strokeWidth={1.5} aria-hidden="true" />
        </Link>
      ) : null}

      {/* The switch springs back on failure, which on its own reads as a
          misfired tap. Say what happened, and leave it said. */}
      {failure ? (
        <p role="alert" className="mt-3 text-sm text-danger-500">
          {failure}
        </p>
      ) : null}
    </section>
  );
}
